import { STORE_PREFERENCES_START_PARAMETER } from "./bot-command-routes";

export const PERSONAL_STORES_FILTER = "__personal_stores__";
export const PERSONAL_CATEGORIES_FILTER = "__personal_categories__";
export const STORE_NOTIFICATIONS_START_PARAMETER = "store_notifications";
export const DIGEST_STORE_SETUP_START_PARAMETER =
  "ads_digest_store_setup_01";
export const PERSONAL_FILTERS_STORAGE_KEY = "kiloprice.personal-filters.v1";
export const PERSONAL_FILTER_MODES_STORAGE_KEY =
  "kiloprice.personal-filter-modes.v1";

export const KNOWN_STORES: readonly string[] = [
  "Linella",
  "Kaufland",
  "Nr1",
  "Local",
  "Family Market",
  "METRO",
  "CIP Market",
  "Rogob",
  "Ocean Fish",
  "Jardi",
  "Zolușca",
  "Casa Curată",
];

const STORE_PRIORITY = new Map(
  KNOWN_STORES.map((store, index) => [store, index]),
);

export function compareStoresByPriority(
  left: string,
  right: string,
  locale = "ru",
): number {
  const leftPriority = STORE_PRIORITY.get(left) ?? Number.MAX_SAFE_INTEGER;
  const rightPriority = STORE_PRIORITY.get(right) ?? Number.MAX_SAFE_INTEGER;
  return leftPriority - rightPriority || left.localeCompare(right, locale);
}

export interface StoreFilterOption {
  value: string;
  active: boolean;
}

export interface PersonalFilterPreferences {
  stores: string[];
  categoryIds: string[];
  catalogNotificationStores: string[];
}

export type PersonalFilterMode = "all" | "personal";

export interface PersonalFilterModes {
  stores: PersonalFilterMode;
  categories: PersonalFilterMode;
}

const EMPTY_PREFERENCES: PersonalFilterPreferences = {
  stores: [],
  categoryIds: [],
  catalogNotificationStores: [],
};

const DEFAULT_FILTER_MODES: PersonalFilterModes = {
  stores: "all",
  categories: "all",
};

export function isStorePreferencesStartParameter(
  value: string | null,
): boolean {
  return (
    value === STORE_NOTIFICATIONS_START_PARAMETER ||
    value === DIGEST_STORE_SETUP_START_PARAMETER ||
    value === STORE_PREFERENCES_START_PARAMETER
  );
}

export function hasPreferenceDraftChanges(
  savedValues: readonly string[],
  draftValues: ReadonlySet<string>,
  notifications?: {
    saved: readonly string[];
    draft: ReadonlySet<string>;
  },
): boolean {
  const selectionChanged =
    savedValues.length !== draftValues.size ||
    savedValues.some((value) => !draftValues.has(value));
  const notificationsChanged =
    notifications !== undefined &&
    (notifications.saved.length !== notifications.draft.size ||
      notifications.saved.some(
        (value) => !notifications.draft.has(value),
      ));
  return (
    selectionChanged ||
    notificationsChanged
  );
}

function cleanValues(value: unknown): string[] {
  if (!Array.isArray(value)) {
    return [];
  }
  const result: string[] = [];
  const seen = new Set<string>();
  for (const item of value) {
    if (typeof item !== "string") {
      continue;
    }
    const cleaned = item.trim();
    if (!cleaned || cleaned.length > 80 || seen.has(cleaned)) {
      continue;
    }
    seen.add(cleaned);
    result.push(cleaned);
    if (result.length >= 64) {
      break;
    }
  }
  return result;
}

export function parsePersonalFilterPreferences(
  value: string | null,
): PersonalFilterPreferences {
  if (!value) {
    return { ...EMPTY_PREFERENCES };
  }
  try {
    const parsed = JSON.parse(value) as Record<string, unknown>;
    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
      return { ...EMPTY_PREFERENCES };
    }
    const stores = cleanValues(parsed.stores);
    const selectedStores = new Set(stores);
    const hasIndividualSettings = Object.prototype.hasOwnProperty.call(
      parsed,
      "catalogNotificationStores",
    );
    const catalogNotificationStores = (
      hasIndividualSettings
        ? cleanValues(parsed.catalogNotificationStores)
        : parsed.catalogNotifications === false
          ? []
          : stores
    ).filter((store) => selectedStores.has(store));
    return {
      stores,
      categoryIds: cleanValues(parsed.categoryIds),
      catalogNotificationStores,
    };
  } catch {
    return { ...EMPTY_PREFERENCES };
  }
}

export function serializePersonalFilterPreferences(
  preferences: PersonalFilterPreferences,
): string {
  const stores = cleanValues(preferences.stores);
  const selectedStores = new Set(stores);
  const catalogNotificationStores = cleanValues(
    preferences.catalogNotificationStores,
  ).filter((store) => selectedStores.has(store));
  return JSON.stringify({
    stores,
    categoryIds: cleanValues(preferences.categoryIds),
    catalogNotificationStores,
    // Old cached clients understand only the global flag. Keeping it derived
    // makes the transition harmless while the current client uses the list.
    catalogNotifications: catalogNotificationStores.length > 0,
  });
}

export function parsePersonalFilterModes(
  value: string | null,
): PersonalFilterModes {
  if (!value) {
    return { ...DEFAULT_FILTER_MODES };
  }
  try {
    const parsed = JSON.parse(value) as Record<string, unknown>;
    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
      return { ...DEFAULT_FILTER_MODES };
    }
    return {
      stores: parsed.stores === "personal" ? "personal" : "all",
      categories: parsed.categories === "personal" ? "personal" : "all",
    };
  } catch {
    return { ...DEFAULT_FILTER_MODES };
  }
}

export function serializePersonalFilterModes(
  modes: PersonalFilterModes,
): string {
  return JSON.stringify({
    stores: modes.stores === "personal" ? "personal" : "all",
    categories: modes.categories === "personal" ? "personal" : "all",
  });
}

export function storeDirectory(
  activeStores: readonly string[],
  savedStores: readonly string[],
): string[] {
  const known = new Set(KNOWN_STORES);
  const extras = [...new Set([...activeStores, ...savedStores])]
    .filter((store) => store && !known.has(store))
    .sort((left, right) => left.localeCompare(right, "ru"));
  return [...KNOWN_STORES, ...extras];
}

export function storeFilterOptions(
  activeStores: readonly string[],
): StoreFilterOption[] {
  const active = new Set(activeStores.filter(Boolean));
  const known = new Set(KNOWN_STORES);
  const activeKnown = KNOWN_STORES.filter((store) => active.has(store));
  const activeExtras = [...active]
    .filter((store) => !known.has(store))
    .sort((left, right) => left.localeCompare(right, "ru"));
  const inactiveKnown = KNOWN_STORES.filter((store) => !active.has(store));

  return [
    ...activeKnown.map((value) => ({ value, active: true })),
    ...activeExtras.map((value) => ({ value, active: true })),
    ...inactiveKnown.map((value) => ({ value, active: false })),
  ];
}

export function selectedOptionsFirst<T extends { value: string }>(
  options: readonly T[],
  selectedValues: ReadonlySet<string>,
): T[] {
  return [
    ...options.filter((option) => selectedValues.has(option.value)),
    ...options.filter((option) => !selectedValues.has(option.value)),
  ];
}
